<template>
  <Modal :open="isOpen" size="lg" :title="modalTitle" :description="modalDescription" @close="handleClose">
    <template #header>
      <div>
        <h3 class="font-heading text-lg font-bold text-white">{{ modalTitle }}</h3>
        <p class="text-sm text-white/50 mt-1">{{ modalDescription }}</p>

        <div v-if="!success && !paymentPending && !signupFailed" class="flex items-center gap-2 mt-4">
          <div
            v-for="s in totalSteps"
            :key="s"
            class="flex items-center gap-2"
            :class="s < totalSteps ? 'flex-1' : ''"
          >
            <div
              class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors"
              :class="s <= step ? 'text-white' : 'bg-white/10 text-white/40'"
              :style="s <= step ? { background: 'linear-gradient(135deg,#ff4d18,#c22700)' } : {}"
            >
              {{ s }}
            </div>
            <div v-if="s < totalSteps" class="h-0.5 flex-1 rounded-full" :class="s < step ? 'bg-primary-500' : 'bg-white/10'"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Sucesso -->
    <div v-if="success" class="text-center py-4 min-h-[420px] flex flex-col justify-center">
      <div class="w-14 h-14 rounded-full bg-success-500/15 text-success-500 flex items-center justify-center mx-auto mb-4">
        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h4 class="font-heading text-white text-lg font-bold mb-2">Cadastro criado com sucesso!</h4>
      <p class="text-sm text-white/60 leading-relaxed mb-6">
        Enviamos um e-mail de confirmação para <strong class="text-white">{{ form.admin_email }}</strong>.
        Use o e-mail e a senha cadastrados para acessar o sistema.
      </p>
      <Button tag="a" :href="`${appUrl}/login`" size="lg" class="w-full justify-center">
        Ir para o login
      </Button>
    </div>

    <!-- Retorno do pagamento (Stripe redireciona de volta pra cá) -->
    <div v-else-if="signupFailed" class="text-center py-4 min-h-[420px] flex flex-col justify-center">
      <div class="w-14 h-14 rounded-full bg-red-500/15 text-red-400 flex items-center justify-center mx-auto mb-4">
        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
      </div>
      <h4 class="font-heading text-white text-lg font-bold mb-2">Pagamento confirmado, mas houve um problema</h4>
      <p class="text-sm text-white/60 leading-relaxed mb-6">
        Recebemos seu pagamento, mas não foi possível concluir a criação da conta automaticamente. Nossa equipe já
        foi avisada e vai finalizar o cadastro<template v-if="returnEmail">, vinculado ao e-mail
        <strong class="text-white">{{ returnEmail }}</strong></template>. Se preferir, fale com o suporte<template
        v-if="returnEmail"> informando esse e-mail</template>.
      </p>
    </div>

    <div v-else-if="paymentPending" class="text-center py-4 min-h-[420px] flex flex-col justify-center">
      <div class="w-14 h-14 rounded-full bg-success-500/15 text-success-500 flex items-center justify-center mx-auto mb-4">
        <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h4 class="font-heading text-white text-lg font-bold mb-2">Pagamento confirmado!</h4>
      <p class="text-sm text-white/60 leading-relaxed mb-6">
        Sua conta está sendo criada. Assim que estiver pronta (em poucos instantes), você recebe um e-mail com os
        dados de acesso.
      </p>
      <Button tag="a" :href="`${appUrl}/login`" size="lg" class="w-full justify-center">
        Ir para o login
      </Button>
    </div>

    <!-- Formulário — altura fixa (referência: step de pagamento) pra o modal
         não pular de tamanho entre os passos. -->
    <form v-else @submit.prevent="handleSubmit" class="min-h-[420px]">
      <p v-if="apiError" class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-4">
        {{ apiError }}
      </p>

      <!-- Step 1: Negócio + Endereço -->
      <div v-show="step === 1" class="space-y-4">
        <div>
          <label class="field-label">Nome do seu negócio *</label>
          <input v-model="form.name" type="text" class="field-input" :placeholder="signupCopy.businessNamePlaceholder" />
        </div>
        <div>
          <label class="field-label">CPF/CNPJ *</label>
          <input
            :value="form.document"
            @input="form.document = maskDocument($event.target.value); documentError = ''"
            @keydown="blockInvalidDocumentKey"
            @blur="validateDocument"
            type="text"
            maxlength="18"
            class="field-input"
            placeholder="000.000.000-00 ou CNPJ"
          />
          <p v-if="documentError" class="text-xs text-red-400 mt-1">{{ documentError }}</p>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="field-label">CEP *</label>
            <input
              :value="form.zip_code"
              @input="form.zip_code = maskCep($event.target.value)"
              @blur="handleCepBlur"
              type="text"
              inputmode="numeric"
              maxlength="9"
              class="field-input"
              placeholder="00000-000"
            />
          </div>
          <div>
            <label class="field-label">Estado (UF) *</label>
            <input
              :value="form.state"
              @input="form.state = $event.target.value.toUpperCase().slice(0, 2)"
              type="text"
              maxlength="2"
              class="field-input"
              placeholder="SP"
            />
          </div>
        </div>
        <p v-if="cepError" class="text-xs text-red-400">{{ cepError }}</p>

        <div class="grid grid-cols-3 gap-3">
          <div class="col-span-2">
            <label class="field-label">Rua *</label>
            <input v-model="form.address" type="text" class="field-input" />
          </div>
          <div>
            <label class="field-label">Número *</label>
            <input v-model="form.address_number" type="text" class="field-input" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="field-label">Bairro *</label>
            <input v-model="form.neighborhood" type="text" class="field-input" />
          </div>
          <div>
            <label class="field-label">Cidade *</label>
            <input v-model="form.city" type="text" class="field-input" />
          </div>
        </div>
      </div>

      <!-- Step 2: Estabelecimento + acesso -->
      <div v-show="step === 2" class="space-y-4">
        <div>
          <label class="field-label">Nome do estabelecimento *</label>
          <input v-model="form.establishment_name" type="text" class="field-input" placeholder="Nome exibido para seus clientes" />
        </div>
        <div>
          <label class="field-label">Seu nome *</label>
          <input v-model="form.admin_name" type="text" class="field-input" />
        </div>
        <div>
          <label class="field-label">Seu e-mail *</label>
          <input v-model="form.admin_email" type="email" class="field-input" placeholder="voce@email.com" />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="field-label">Senha *</label>
            <input v-model="form.admin_password" type="password" class="field-input" placeholder="Crie uma senha" />
          </div>
          <div>
            <label class="field-label">Confirmar senha *</label>
            <input v-model="form.confirm_password" type="password" class="field-input" />
          </div>
        </div>
        <ul class="space-y-1">
          <li
            v-for="rule in PASSWORD_RULES"
            :key="rule.key"
            class="flex items-center gap-1.5 text-xs"
            :class="rule.test(form.admin_password) ? 'text-success-400' : 'text-white/40'"
          >
            <svg v-if="rule.test(form.admin_password)" class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
            <span v-else class="w-3 h-3 shrink-0 flex items-center justify-center">
              <span class="w-1 h-1 rounded-full bg-white/40"></span>
            </span>
            {{ rule.label }}
          </li>
        </ul>
        <p v-if="form.confirm_password && form.admin_password !== form.confirm_password" class="text-xs text-red-400">
          As senhas não conferem.
        </p>

        <label class="flex items-start gap-2.5 pt-1">
          <input v-model="form.terms_accepted" type="checkbox" class="mt-0.5 accent-primary-500" />
          <span class="text-xs text-white/50 leading-relaxed">
            Li e aceito os
            <a
              :href="`${appUrl}/termos`"
              target="_blank"
              rel="noopener"
              class="text-primary-400 hover:underline"
              @click="onLegalLinkClick($event, 'terms')"
            >Termos de Uso</a>
            e a
            <a
              :href="`${appUrl}/privacidade`"
              target="_blank"
              rel="noopener"
              class="text-primary-400 hover:underline"
              @click="onLegalLinkClick($event, 'privacy')"
            >Política de Privacidade</a>.
          </span>
        </label>
      </div>

      <!-- Step 3: Plano -->
      <div v-show="step === 3" class="space-y-3">
        <p class="text-sm text-white/60 mb-1">Escolha o plano ideal para o seu negócio</p>
        <p v-if="plansLoading" class="text-sm text-white/50">Carregando planos...</p>
        <p v-else-if="!plans.length" class="text-sm text-white/50">
          Nenhum plano disponível no momento. Você pode continuar e escolher depois.
        </p>
        <button
          v-for="plan in plans"
          :key="plan.id"
          type="button"
          class="w-full text-left rounded-xl border px-4 py-3 transition-colors"
          :class="form.plan_id === plan.id ? 'border-primary-500 bg-primary-500/10' : 'border-white/10 hover:border-white/20'"
          @click="form.plan_id = plan.id"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-heading font-bold text-white text-sm">{{ plan.name }}</span>
            <span class="text-sm font-bold text-white">{{ planPriceFor(plan).priceLabel }}</span>
          </div>
        </button>

        <div v-if="form.plan_id" class="flex items-center gap-1 bg-white/5 rounded-xl p-1 w-fit mt-2">
          <button
            type="button"
            @click="form.billing_cycle = 'MONTHLY'"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150"
            :class="form.billing_cycle === 'MONTHLY' ? 'text-white' : 'text-white/50 hover:text-white/80'"
            :style="form.billing_cycle === 'MONTHLY' ? { background: 'linear-gradient(135deg,#ff4d18,#c22700)' } : {}"
          >
            Mensal
          </button>
          <button
            v-if="selectedPlan?.price_yearly != null"
            type="button"
            @click="form.billing_cycle = 'YEARLY'"
            class="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-150"
            :class="form.billing_cycle === 'YEARLY' ? 'text-white' : 'text-white/50 hover:text-white/80'"
            :style="form.billing_cycle === 'YEARLY' ? { background: 'linear-gradient(135deg,#ff4d18,#c22700)' } : {}"
          >
            Anual
          </button>
        </div>
      </div>

      <!-- Step 4: Pagamento (só existe quando um plano foi escolhido) -->
      <div v-show="step === paymentStep" class="space-y-3">
        <p v-if="hasTrial && paymentMethod === 'card'" class="text-sm text-white/60 mb-1">
          Teste grátis por {{ paymentOptions.trialDays }} dias. Informe seu cartão agora: a primeira cobrança de {{ checkoutSubmitPriceLabel }} só acontece 1 dia depois do fim do teste, e você pode cancelar antes.
        </p>
        <p v-else class="text-sm text-white/60 mb-1">Finalize o pagamento para ativar sua conta</p>

        <!-- Seletor Cartão / Pix (só quando o Pix está disponível) -->
        <div v-if="paymentOptions.pix" class="space-y-2">
          <p class="text-xs font-semibold text-white/50">Como você quer pagar?</p>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              @click="selectPaymentMethod('card')"
              class="rounded-xl border px-4 py-3 text-sm font-medium transition-colors"
              :class="paymentMethod === 'card' ? 'border-primary-500 bg-primary-500/10 text-white' : 'border-white/10 text-white/60 hover:border-white/20'"
            >
              Cartão de crédito
            </button>
            <button
              type="button"
              @click="selectPaymentMethod('pix')"
              class="rounded-xl border px-4 py-3 text-sm font-medium transition-colors"
              :class="paymentMethod === 'pix' ? 'border-primary-500 bg-primary-500/10 text-white' : 'border-white/10 text-white/60 hover:border-white/20'"
            >
              Pix
            </button>
          </div>
        </div>

        <!-- Cartão (Stripe) -->
        <template v-if="paymentMethod === 'card'">
          <p v-if="checkoutError" class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {{ checkoutError }}
          </p>
          <div v-else>
            <div class="relative min-h-[220px]">
              <p v-if="!checkoutMounted" class="text-sm text-white/50 text-center py-10">Carregando formulário de pagamento...</p>
              <div ref="checkoutContainer" />
            </div>
            <p v-if="checkoutConfirmError" class="text-xs text-red-400 mt-2">{{ checkoutConfirmError }}</p>
          </div>
        </template>

        <!-- Pix (Mercado Pago) -->
        <template v-else>
          <p v-if="pixError" class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {{ pixError }}
          </p>
          <div v-else-if="pixCharge" class="space-y-3">
            <img
              v-if="pixCharge.qr_code_base64"
              :src="`data:image/png;base64,${pixCharge.qr_code_base64}`"
              alt="QR Code Pix"
              class="w-44 h-44 rounded-lg border border-white/10 bg-white mx-auto"
            />
            <div>
              <label class="field-label">Pix copia e cola</label>
              <div class="flex items-center gap-2">
                <input :value="pixCharge.qr_code" readonly class="field-input !text-xs" />
                <button type="button" class="shrink-0 px-3 py-2 text-xs rounded-lg bg-white/10 text-white hover:bg-white/20" @click="copyPixCode">
                  Copiar
                </button>
              </div>
            </div>
            <a
              v-if="pixCharge.ticket_url"
              :href="pixCharge.ticket_url"
              target="_blank"
              rel="noopener"
              class="text-xs text-primary-400 hover:underline"
            >
              Abrir no Mercado Pago
            </a>
            <p class="text-xs text-white/50 flex items-center gap-2">
              <span class="inline-block w-3 h-3 rounded-full border-2 border-white/30 border-t-white/70 animate-spin" />
              Aguardando confirmação do pagamento…
            </p>
          </div>
          <p v-else class="text-sm text-white/50 text-center py-10">
            Clique em "Gerar Pix" para criar o código de pagamento.
          </p>
        </template>
      </div>
    </form>

    <template v-if="!success && !paymentPending && !signupFailed" #footer>
      <div class="flex items-center justify-between gap-3">
        <Button
          v-if="step > 1"
          type="button"
          variant="secondary"
          class="!text-white !border-white/20 hover:!bg-white/10"
          @click="goToPrevStep"
        >
          Voltar
        </Button>
        <span v-else></span>

        <Button
          v-if="step < totalSteps"
          type="button"
          :disabled="!stepValid"
          @click="goToNextStep"
        >
          Continuar
        </Button>
        <Button
          v-else-if="!form.plan_id"
          type="button"
          :disabled="!stepValid || submitting"
          @click="handleSubmit"
        >
          {{ submitting ? 'Criando conta...' : 'Criar minha conta' }}
        </Button>
        <Button
          v-else-if="paymentMethod === 'card'"
          type="button"
          :disabled="!checkoutMounted || checkoutConfirming"
          @click="confirmCheckoutPayment"
        >
          {{ checkoutConfirming ? 'Processando...' : checkoutSubmitLabel }}
        </Button>
        <Button
          v-else-if="!pixCharge"
          type="button"
          :disabled="pixStarting"
          @click="startSignupPix"
        >
          {{ pixStarting ? 'Gerando...' : `Gerar Pix de ${checkoutSubmitPriceLabel}` }}
        </Button>
        <Button
          v-else
          type="button"
          :disabled="pixChecking"
          @click="checkPixNow"
        >
          {{ pixChecking ? 'Verificando...' : 'Já paguei' }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, nextTick } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import Modal from '../ui/Modal.vue'
import Button from '../ui/Button.vue'
import { useSignupModal } from '../../composables/useSignupModal.js'
import { useLegalModal } from '../../composables/useLegalModal.js'
import { useCep } from '../../composables/useCep.js'
import { maskDocument } from '../../utils/masks.js'
import { isValidCpfOrCnpj } from '../../utils/documentValidation.js'
import { apiUrl, appUrl, signupCopy } from '../../content/landingContent.js'
import { trackEvent } from '../../utils/analytics.js'
import { fetchPlans } from '../../utils/fetchPlans.js'
import { finalizeSignup } from '../../utils/finalizeSignup.js'
import { fetchPaymentOptions } from '../../utils/fetchPaymentOptions.js'
import { startPixSignup, pollPixSignup } from '../../utils/pixSignup.js'
import { formatPlanPrice } from '../../utils/planPricing.js'
import { PASSWORD_RULES, isPasswordValid } from '../../utils/passwordPolicy.js'

const { isOpen, preselectedPlanId, closeSignupModal } = useSignupModal()

const { openLegalModal } = useLegalModal()

// Abre o texto por cima do cadastro em vez de mandar pra outra aba, que
// tirava o usuário do meio do wizard (o modal legal sobe acima deste, ver
// z-class em LegalDocumentModal.vue).
const onLegalLinkClick = (event, doc) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
  event.preventDefault()
  openLegalModal(doc)
}
const { cepError, maskCep, lookupCep } = useCep()

const step = ref(1)
const success = ref(false)
const submitting = ref(false)
const apiError = ref('')

const plans = ref([])
const plansLoading = ref(false)

// Passos: 1) Negócio + Endereço · 2) Estabelecimento + acesso · 3) Plano ·
// 4) Pagamento. O passo de pagamento só existe quando um plano foi escolhido —
// sem plano, o cadastro segue direto (POST /api/public/tenants, sem cobrança).
const totalSteps = computed(() => form.plan_id ? 4 : 3)
// Índice do passo de pagamento (o último, quando há plano); -1 quando não há.
const paymentStep = computed(() => form.plan_id ? totalSteps.value : -1)

// Checkout customizado do Stripe (step 5, Payment Element via Elements) —
// mesmo padrão usado em Configuracoes.vue (appointments-frontend), só que
// aqui roda antes do tenant existir: o pagamento confirma via webhook, que
// só então cria a conta (ver stripeWebhook.js).
const checkoutError = ref('')
const checkoutConfirmError = ref('')
const checkoutConfirming = ref(false)
const checkoutMounted = ref(false)
const checkoutContainer = ref(null)
let checkout = null
let paymentElement = null
let stripeClient = null

// True quando o Stripe redireciona de volta pra cá após o pagamento
// (return_url=/?signup=success) — a conta já está sendo criada pelo
// webhook nesse momento, de forma assíncrona.
const paymentPending = ref(false)

// ── Pix (Mercado Pago) ──────────────────────────────────────────────────
// Alternativa ao cartão no step 5, quando o backend indica que o Pix está
// disponível. Sem redirect: gera uma cobrança e faz polling até confirmar.
const paymentOptions = reactive({ card: true, pix: false, trialDays: 0 })
const paymentMethod = ref('card')
const pixCharge = ref(null)
const pixError = ref('')
const pixStarting = ref(false)
const pixChecking = ref(false)
let pixPollTimer = null

const checkoutSubmitPriceLabel = computed(() => {
  const plan = plans.value.find((p) => p.id === form.plan_id)
  const price = plan ? (form.billing_cycle === 'YEARLY' ? plan.price_yearly : plan.price_monthly) : 0
  return Number(price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
})

function stopPixPolling() {
  if (pixPollTimer) { clearInterval(pixPollTimer); pixPollTimer = null }
}

function handlePixStatus(status) {
  if (status === 'completed') {
    stopPixPolling()
    paymentPending.value = true
  } else if (status === 'failed') {
    stopPixPolling()
    paymentPending.value = false
    signupFailed.value = true
  }
}

async function startSignupPix() {
  pixError.value = ''
  pixStarting.value = true
  try {
    const res = await startPixSignup(form)
    if (!res.ok) {
      pixError.value = res.error
      trackEvent('signup_pix_error')
      return
    }
    pixCharge.value = res
    stopPixPolling()
    pixPollTimer = setInterval(async () => {
      const { status } = await pollPixSignup(res.external_reference)
      handlePixStatus(status)
    }, 5000)
  } finally {
    pixStarting.value = false
  }
}

async function checkPixNow() {
  if (!pixCharge.value?.external_reference || pixChecking.value) return
  pixChecking.value = true
  try {
    const { status } = await pollPixSignup(pixCharge.value.external_reference)
    handlePixStatus(status)
  } finally {
    pixChecking.value = false
  }
}

async function copyPixCode() {
  if (!pixCharge.value?.qr_code) return
  try { await navigator.clipboard.writeText(pixCharge.value.qr_code) } catch { /* clipboard indisponível */ }
}

function selectPaymentMethod(method) {
  if (paymentMethod.value === method) return
  paymentMethod.value = method
  if (method === 'pix') {
    destroyCheckout()
  } else {
    stopPixPolling()
    pixCharge.value = null
    pixError.value = ''
    if (step.value === paymentStep.value) startSignupCheckout()
  }
}

// True quando a reconciliação no retorno indica que o cadastro pago não
// pôde ser concluído (o backend já avisou o time) — ver finalizeSignup.js.
const signupFailed = ref(false)
// E-mail do cadastro, recuperado do backend no retorno (o form já foi
// resetado nesse ponto) — exibido na tela de falha.
const returnEmail = ref('')

onMounted(async () => {
  plansLoading.value = true
  plans.value = await fetchPlans()
  plansLoading.value = false

  fetchPaymentOptions().then((opts) => {
    paymentOptions.card = opts.card
    paymentOptions.pix = opts.pix
    paymentOptions.trialDays = opts.trialDays
  })

  const params = new URLSearchParams(window.location.search)
  if (params.get('signup') === 'success') {
    paymentPending.value = true
    isOpen.value = true

    // Rede de segurança: se o webhook checkout.session.completed não chegar,
    // esta chamada finaliza o cadastro pelo próprio retorno do checkout.
    const sessionId = params.get('session_id')
    window.history.replaceState({}, '', window.location.pathname)
    if (sessionId) {
      const { status, email } = await finalizeSignup(sessionId)
      if (email) returnEmail.value = email
      if (status === 'failed') {
        paymentPending.value = false
        signupFailed.value = true
      }
    }
  }
})

const emptyForm = () => ({
  name: '',
  document: '',
  zip_code: '',
  state: '',
  address: '',
  address_number: '',
  neighborhood: '',
  city: '',
  establishment_name: '',
  admin_name: '',
  admin_email: '',
  admin_password: '',
  confirm_password: '',
  terms_accepted: false,
  plan_id: null,
  billing_cycle: 'MONTHLY',
})

const form = reactive(emptyForm())

// Bloqueia a digitação de caracteres que não podem aparecer em CPF nem em
// CNPJ alfanumérico (IN RFB 2.119/2022) no campo de CPF/CNPJ (o @input com
// maskDocument já limpa o valor, isto barra na origem).
const blockInvalidDocumentKey = (e) => {
  if (e.ctrlKey || e.metaKey || e.key.length > 1) return
  if (!/[0-9a-zA-Z]/.test(e.key)) e.preventDefault()
}

const documentError = ref('')
const validateDocument = () => {
  if (!form.document) { documentError.value = ''; return true }
  const ok = isValidCpfOrCnpj(form.document)
  documentError.value = ok ? '' : 'CPF/CNPJ inválido'
  return ok
}

const modalTitle = computed(() => {
  if (signupFailed.value) return 'Quase lá'
  if (success.value || paymentPending.value) return 'Tudo pronto!'
  return 'Comece agora'
})
const modalDescription = computed(() => (success.value || paymentPending.value || signupFailed.value)
  ? ''
  : signupCopy.modalDescription)

const stepValid = computed(() => {
  // Step 1: negócio + endereço
  if (step.value === 1) {
    return (
      form.name.trim().length > 0 &&
      isValidCpfOrCnpj(form.document) &&
      form.zip_code.replace(/\D/g, '').length === 8 &&
      form.state.length === 2 &&
      form.address.trim().length > 0 &&
      form.address_number.trim().length > 0 &&
      form.neighborhood.trim().length > 0 &&
      form.city.trim().length > 0
    )
  }
  // Step 2: estabelecimento + acesso
  if (step.value === 2) {
    return (
      form.establishment_name.trim().length > 0 &&
      form.admin_name.trim().length > 1 &&
      /^\S+@\S+\.\S+$/.test(form.admin_email) &&
      isPasswordValid(form.admin_password) &&
      form.admin_password === form.confirm_password &&
      form.terms_accepted === true
    )
  }
  // Step 3: plano
  return plans.value.length === 0 || !!form.plan_id
})

// Mesma paleta/tema escuro usado no checkout de Configuracoes.vue
// (appointments-frontend) — o modal aqui também é sempre escuro, então usamos
// o tema 'night' da Stripe em vez do claro pra combinar com o resto da marca.
function buildStripeAppearance() {
  return {
    theme: 'night',
    variables: {
      colorPrimary: '#ff4d18',
      colorBackground: '#18181b',
      colorText: '#e4e4e7',
      colorDanger: '#d21e1e',
      fontFamily: 'Sora, system-ui, sans-serif',
      borderRadius: '10px',
      spacingUnit: '4px',
    },
  }
}

const selectedPlan = computed(() => plans.value.find((p) => p.id === form.plan_id) || null)

// Preço exibido no card: o ciclo escolhido (Mensal/Anual) só vale pro plano
// selecionado; os demais seguem mostrando o mensal como referência.
const planPriceFor = (plan) => formatPlanPrice(plan, form.plan_id === plan.id ? form.billing_cycle : 'MONTHLY')

// Plano sem preço anual (opcional, ver migration 034) não pode ficar com
// "Anual" selecionado — o botão correspondente some (ver template) e o
// ciclo volta pra mensal automaticamente se estava marcado.
watch(selectedPlan, (plan) => {
  if (plan && plan.price_yearly == null) form.billing_cycle = 'MONTHLY'
})

// Cartão com período de teste: nada é cobrado no cadastro, só o cartão é
// guardado. A cobrança ocorre 1 dia depois do fim do teste.
const hasTrial = computed(() => paymentOptions.trialDays > 0)

const checkoutSubmitLabel = computed(() => {
  if (hasTrial.value) return `Começar teste grátis de ${paymentOptions.trialDays} dias`
  const plan = plans.value.find((p) => p.id === form.plan_id)
  if (!plan) return 'Pagar agora'
  const price = form.billing_cycle === 'YEARLY' ? plan.price_yearly : plan.price_monthly
  const priceLabel = Number(price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  return `Pagar ${priceLabel} agora`
})

function destroyCheckout() {
  if (paymentElement) {
    paymentElement.destroy()
    paymentElement = null
  }
  checkout = null
  checkoutMounted.value = false
  checkoutConfirmError.value = ''
}

const startSignupCheckout = async () => {
  checkoutError.value = ''
  destroyCheckout()

  try {
    const res = await fetch(`${apiUrl}/api/public/tenants/checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        document: form.document,
        address: form.address,
        address_number: form.address_number,
        neighborhood: form.neighborhood,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        establishment_name: form.establishment_name,
        admin_name: form.admin_name,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
        plan_id: form.plan_id,
        billing_cycle: form.billing_cycle,
        terms_accepted: form.terms_accepted,
      }),
    })

    const data = await res.json()

    if (!res.ok) {
      checkoutError.value = data.error || 'Não foi possível iniciar o pagamento. Tente novamente.'
      trackEvent('signup_checkout_error', { status: res.status })
      return
    }

    if (!data.publishable_key) {
      checkoutError.value = 'Pagamento indisponível no momento. Tente novamente mais tarde.'
      return
    }

    if (!stripeClient) {
      stripeClient = await loadStripe(data.publishable_key)
    }
    await nextTick()
    checkout = stripeClient.initCheckoutElementsSdk({
      clientSecret: data.clientSecret,
      elementsOptions: { appearance: buildStripeAppearance() },
    })
    paymentElement = checkout.createPaymentElement()
    paymentElement.on('ready', () => { checkoutMounted.value = true })
    paymentElement.mount(checkoutContainer.value)
  } catch {
    checkoutError.value = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.'
  }
}

const confirmCheckoutPayment = async () => {
  if (!checkout || checkoutConfirming.value) return
  checkoutConfirming.value = true
  checkoutConfirmError.value = ''
  try {
    const actionsResult = await checkout.loadActions()
    if (actionsResult.type === 'error') {
      checkoutConfirmError.value = actionsResult.error.message
      return
    }
    const confirmResult = await actionsResult.actions.confirm()
    if (confirmResult.type === 'error') {
      checkoutConfirmError.value = confirmResult.error.message
    }
    // Em caso de sucesso, o Stripe redireciona sozinho pro return_url (signup=success).
  } catch (err) {
    checkoutConfirmError.value = err.message || 'Erro ao processar pagamento'
  } finally {
    checkoutConfirming.value = false
  }
}

const goToNextStep = async () => {
  if (!stepValid.value) return
  step.value += 1
  if (step.value === paymentStep.value && paymentMethod.value === 'card') {
    await startSignupCheckout()
  }
}

const goToPrevStep = () => {
  if (step.value === paymentStep.value) {
    destroyCheckout()
    stopPixPolling()
    pixCharge.value = null
    pixError.value = ''
  }
  step.value -= 1
}

const handleCepBlur = () => {
  lookupCep(form.zip_code, (data) => {
    form.address = data.address || form.address
    form.neighborhood = data.neighborhood || form.neighborhood
    form.city = data.city || form.city
    form.state = data.state || form.state
    form.zip_code = data.zip_code
  })
}

const handleClose = () => {
  closeSignupModal()
}

// Ao abrir, aplica o plano pré-selecionado (vindo do CTA de um card específico
// na seção de Planos, ver LandingPlans.vue). Ao fechar, reseta o wizard depois
// que o modal termina a transição de saída (evita o usuário ver o form
// voltando ao passo 1 durante a animação).
watch(isOpen, (open) => {
  if (open) {
    if (preselectedPlanId.value) form.plan_id = preselectedPlanId.value
    return
  }
  destroyCheckout()
  stopPixPolling()
  setTimeout(() => {
    step.value = 1
    success.value = false
    paymentPending.value = false
    signupFailed.value = false
    returnEmail.value = ''
    apiError.value = ''
    checkoutError.value = ''
    documentError.value = ''
    pixCharge.value = null
    pixError.value = ''
    paymentMethod.value = 'card'
    Object.assign(form, emptyForm())
  }, 250)
})

const handleSubmit = async () => {
  // O <form> engloba os passos do wizard, então Enter num campo também dispara
  // @submit — nesse caso só avança o wizard, não chama a API.
  if (step.value < totalSteps.value) {
    if (stepValid.value) await goToNextStep()
    return
  }

  // Com plano escolhido, o passo final é o pagamento — a criação da conta
  // acontece via webhook depois que o pagamento confirma, não aqui.
  if (form.plan_id) return

  if (!stepValid.value || submitting.value) return

  submitting.value = true
  apiError.value = ''

  try {
    const res = await fetch(`${apiUrl}/api/public/tenants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        document: form.document,
        address: form.address,
        address_number: form.address_number,
        neighborhood: form.neighborhood,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        establishment_name: form.establishment_name,
        admin_name: form.admin_name,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
        plan_id: form.plan_id,
        terms_accepted: form.terms_accepted,
      }),
    })

    const data = await res.json()

    if (!res.ok) {
      apiError.value = data.error || 'Não foi possível concluir o cadastro. Tente novamente.'
      trackEvent('signup_error', { status: res.status })
      return
    }

    success.value = true
    trackEvent('signup_success')
  } catch {
    apiError.value = 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.375rem;
}

.field-input {
  width: 100%;
  border-radius: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 0.875rem;
  padding: 0.625rem 0.75rem;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.field-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.field-input:focus {
  outline: none;
  border-color: #ff4d18;
  background: rgba(255, 255, 255, 0.08);
}
</style>
